/**
 * Reports — the downloadable match reports Priori published for this club,
 * beneath the featured promotions on /insights.
 *
 * FAILS TO NOTHING, like InsightsFeatured: an absent or refused endpoint
 * renders no section rather than an error, so the rest of the page is
 * unaffected.
 *
 * View and Download are anchors, not fetches. View opens the in-app viewer
 * (/insights/reports/<id>, pages/ReportViewer) in a new tab, which shows a
 * loading state while the page arrives; Download saves the file under its
 * own name (attachment).
 *
 * A LIST, NOT CARDS (Ben, 2026-10-26; the card grid of 2026-10-24 is gone).
 * Rows group by match (every insight for one game together) or by insight
 * (one question across every game). The grouping choice is kept in this
 * browser. Insights Feed cleanup (Ben, 2026-10-26):
 *  - matches run newest MATCH first (its date, not its newest report);
 *  - a match heads with its date and result before its insight count;
 *  - a match's insights read in one order for every match, the most
 *    general first (the server's PACK_ORDER, served as `order`);
 *  - the date column is headed Report Date: it is when the report was
 *    built, not when the match was played.
 */
import { useEffect, useState } from 'react'

import ClubLogo from '../components/ClubLogo'
import {
  clubReportDownloadUrl, clubReportViewUrl, listClubReports, type ClubReport,
} from '../packs/promotions'

import '../components/club-logo.css'
import './insights.css'

const matchDate = (iso: string | null) =>
  iso ? iso.slice(0, 10) : null

export type ReportsView = { group: 'match' | 'insight' }
const VIEW_KEY = 'priori.insights.reports.view'
const DEFAULT_VIEW: ReportsView = { group: 'match' }

export function readView(): ReportsView {
  try {
    const v = JSON.parse(localStorage.getItem(VIEW_KEY) ?? 'null') as Partial<ReportsView> | null
    return { group: v?.group === 'insight' ? 'insight' : 'match' }
  } catch { return DEFAULT_VIEW }
}

/** The order reports read in within a match: the pack's place (most
 *  general first), then title. */
const byPack = (a: ClubReport, b: ClubReport) =>
  (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
  || a.title.localeCompare(b.title)

/** When a match was played, for ordering: its date, else its report's
 *  build day. A match with no date recorded therefore sorts by when its
 *  reports were BUILT — a report built today for an old friendly leads
 *  the feed until someone records the date. Ties break by game code
 *  (release review of 2e03fe86 caught this comment claiming otherwise). */
const playedOn = (r: ClubReport) => r.match_date ?? (r.computed_at ?? '').slice(0, 10)

/** Reports grouped by match (game_code) or by insight (pack).
 *  By match: matches newest played first, each match's reports in pack
 *  order. By insight: insights in pack order, each one's matches newest
 *  played first. */
export function groupReports(reports: ClubReport[], by: 'match' | 'insight') {
  const key = (r: ClubReport) => (by === 'match' ? r.game_code : r.pack_id)
  const groups = new Map<string, ClubReport[]>()
  for (const r of reports) groups.set(key(r), [...(groups.get(key(r)) ?? []), r])
  const newestPlayed = (a: ClubReport, b: ClubReport) =>
    playedOn(b).localeCompare(playedOn(a)) || a.game_code.localeCompare(b.game_code)
  // A match group's "when" is its NEWEST playedOn across every report —
  // computed before byPack reorders the group for display, which otherwise
  // made an undated match sort by whichever report is most general, not by
  // its newest run (release review of 077a7af0, 2026-10-26).
  const groupPlayed = (rs: ClubReport[]) =>
    rs.reduce((m, r) => (playedOn(r) > m ? playedOn(r) : m), '')
  const out = [...groups.entries()].map(([k, rs]) => ({
    key: k,
    label: by === 'match' ? k : rs[0]!.title,
    played: groupPlayed(rs),
    reports: [...rs].sort(by === 'match' ? byPack : newestPlayed),
  }))
  return out.sort((a, b) => (by === 'match'
    ? b.played.localeCompare(a.played) || a.key.localeCompare(b.key)
    : byPack(a.reports[0]!, b.reports[0]!)))
}

/** "2026-10-04 · 3–5": a match's date and result for its heading; either
 *  part left out when unknown, null when both are. The result reads in
 *  the label's order — the game code is AWAY@HOME, so away goals first —
 *  as the Games list reads its score in its columns' order. Home first
 *  beside TENNESSEE@ALABAMA showed 2–1 for a match that was 1–2 (PRI-1975). */
export function matchLine(r: ClubReport): string | null {
  const parts = [r.match_date ?? null,
                 r.score ? `${r.score.away}\u2013${r.score.home}` : null]
    .filter((x): x is string => !!x)
  return parts.length ? parts.join(' \u00b7 ') : null
}

/** LAG@SKC -> ['LAG', 'SKC']; anything unsplittable renders without crests. */
const sides = (gameCode: string): [string, string] | null => {
  const [a, b, ...rest] = gameCode.split('@')
  return a && b && rest.length === 0 ? [a, b] : null
}

export default function InsightsReports({ onCount }: {
  /** How many reports rendered (0 on empty or failure). The parent's
   *  empty-feed copy must not claim the page is empty while this section
   *  is showing a report. */
  onCount?: (n: number) => void
} = {}) {
  const [reports, setReports] = useState<ClubReport[] | null>(null)
  const [view, setViewState] = useState<ReportsView>(readView)
  const setView = (v: ReportsView) => {
    setViewState(v)
    try { localStorage.setItem(VIEW_KEY, JSON.stringify(v)) } catch { /* not kept */ }
  }

  useEffect(() => {
    listClubReports()
      .then((d) => { setReports(d.reports); onCount?.(d.reports.length) })
      .catch(() => { setReports(null); onCount?.(0) })
    // onCount is a setState from the parent; identity churn must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!reports || reports.length === 0) return null

  return (
    <section className="ins-reports">
      <div className="ins-reports__head">
        <h2 className="ins-reports__title">Reports</h2>
        <div className="ins-reports__controls">
          <div className="ins-seg" role="group" aria-label="Group by">
            {(['match', 'insight'] as const).map((g) => (
              <button key={g} type="button" aria-pressed={view.group === g}
                      onClick={() => setView({ ...view, group: g })}>
                {g === 'match' ? 'By match' : 'By insight'}
              </button>
            ))}
          </div>
        </div>
      </div>
      <p className="ins-reports__sub">
        Full match reports prepared for your club. Each one opens in a browser
        and needs no connection.
      </p>
      <div className="ins-report-list">
          {groupReports(reports, view.group).map((grp) => (
            <section key={grp.key} className="ins-report-group"
                     aria-label={grp.label}>
              <h3 className="ins-report-group__title">
                {view.group === 'match' && sides(grp.key) ? (
                  <span className="ins-report-group__matchup" aria-hidden="true">
                    {/* the server resolves each crest through the club's
                        own team-logo mapping, as the Games list does; a
                        guessed /media/org-logo/<code>.png drew initials
                        for TENNESSEE, OLIVET, … (2026-11-03) */}
                    <ClubLogo code={sides(grp.key)![0]} size="sm"
                              url={grp.reports[0]!.away_team_logo ?? null} />
                    <span>@</span>
                    <ClubLogo code={sides(grp.key)![1]} size="sm"
                              url={grp.reports[0]!.home_team_logo ?? null} />
                  </span>
                ) : null}
                <span className={view.group === 'match' ? 'mono' : undefined}>{grp.label}</span>
                {view.group === 'match' && matchLine(grp.reports[0]!) && (
                  <span className="ins-report-group__match mono"
                        data-testid="report-match-line">{matchLine(grp.reports[0]!)}</span>
                )}
                {/* "2-2 11" read as part of the scoreline (Ben,
                    2026-10-27): the count says what it counts. */}
                <span className="ins-report-group__count">
                  · ({grp.reports.length} Report{grp.reports.length === 1 ? '' : 's'})
                </span>
              </h3>
              <ul className="ins-report-rows">
                <li className="ins-report-row ins-report-row--head" aria-hidden="true">
                  <span className="ins-report-row__name">
                    {view.group === 'match' ? 'Insight' : 'Match'}</span>
                  <span className="ins-report-row__date">Report Date</span>
                  <span className="ins-report-row__actions" />
                </li>
                {grp.reports.map((r) => (
                  <li key={r.run_id} className="ins-report-row">
                    <a className="ins-report-row__name" href={clubReportViewUrl(r.run_id)}
                       target="_blank" rel="noopener">
                      {view.group === 'match' ? r.title : <span className="mono">{r.game_code}</span>}
                    </a>
                    <span className="ins-report-row__date mono">{matchDate(r.computed_at) ?? '—'}</span>
                    <span className="ins-report-row__actions">
                      <a href={clubReportViewUrl(r.run_id)} target="_blank" rel="noopener">View</a>
                      <a href={clubReportDownloadUrl(r.run_id)}>Download</a>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
      </div>
    </section>
  )
}
